<template>
	<v-container fluid class="pa-0">
		<v-container fluid class="py-0">
			<!-- Overview header -->
			<v-card flat>
				<v-card-title>
					<v-col cols="auto">
						<h2 class="display-3 font-weight-medium">Secrets</h2>
					</v-col>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-alert v-if="error" tile type="error" class="mb-0">{{
					error
				}}</v-alert>
			</v-card>
		</v-container>

		<v-card flat color="grey lighten-4" min-height="76vh">
			<v-container fluid class="pa-5">
				<!-- filter -->
				<v-card class="mt-0 py-5" elevation="3">
					<v-row align="center" class="px-5">
						<v-col cols="auto">
							<v-autocomplete
								v-model="selectedSubscription"
								:items="subscriptions"
								item-text="displayName"
								item-value="subscriptionId"
								label="Subscription"
								hide-details
								dense
								color="primary"
								class="subtitle-2"
								:loading="isLoadingSubscriptions"
							></v-autocomplete>
						</v-col>
						<v-col cols="auto">
							<v-autocomplete
								v-model="selectedVault"
								:items="vaults"
								item-text="name"
								item-value="properties.vaultUri"
								label="Vault"
								hide-details
								dense
								color="primary"
								class="subtitle-2"
								:loading="isLoadingVaults"
							></v-autocomplete>
						</v-col>
						<v-col cols="auto">
							<v-text-field
								v-model="searchSecret"
								ref="searchSecret"
								label="Search | Ctrl+;"
								hide-details
								dense
								class="subtitle-2"
								clearable
								@click:clear="clearSearchSecret()"
							>
								></v-text-field
							>
						</v-col>
						<v-spacer />
						<v-col cols="auto">
							<v-tooltip
								bottom
								v-if="
									defaultSelection.subscriptionId ===
										selectedSubscription &&
									defaultSelection.vault === selectedVault &&
									defaultSelection.subscriptionId != null
								"
							>
								<template v-slot:activator="{ on }">
									<v-btn
										v-on="on"
										icon
										color="primary"
										@click="deleteDefaultSelection"
										><v-icon>star</v-icon></v-btn
									>
								</template>
								<span> Delete Default </span>
							</v-tooltip>
							<v-tooltip bottom v-else>
								<template v-slot:activator="{ on }">
									<v-btn
										v-on="on"
										icon
										color="primary"
										@click="setDefaultSelection"
										><v-icon>star_border</v-icon></v-btn
									>
								</template>
								<span> Set Default </span>
							</v-tooltip>
							<v-tooltip bottom>
								<template v-slot:activator="{ on }">
									<v-btn
										v-on="on"
										:disabled="!selectedVault.length"
										small
										rounded
										color="primary"
										@click="addSecret"
										>Add</v-btn
									>
								</template>
								<span> Add Secret | Ctrl+, </span>
							</v-tooltip>
						</v-col>
					</v-row>
				</v-card>

				<!-- data table card -->
				<v-card class="mt-3" elevation="3">
					<v-card-text class="pb-3">
						<v-data-table
							:headers="secretHeaders"
							:items="secrets"
							:items-per-page="25"
							:footer-props="{
								'items-per-page-options': [10, 25, 50, 75, 100],
							}"
							:sort-by="['secretName']"
							:search="searchSecret"
							v-model="selectedSecret"
							show-select
							single-select
							item-key="secretId"
							dense
							show-expand
							single-expand
							:loading="isLoadingSecrets"
							no-data-text="Please select a subscription and a vault"
						>
							<template v-slot:[`item.username`]="{ item }">
								<template>
									<v-btn
										v-if="item.username"
										icon
										color="primary"
										small
										@click="copyValue(item.username)"
									>
										<v-icon small>content_copy</v-icon>
									</v-btn>
									{{ item.username }}
								</template>
							</template>

							<template v-slot:[`item.secretValue`]="{ item }">
								<v-btn
									icon
									color="primary"
									small
									@click="copySecretValue(item)"
								>
									<v-icon small>content_copy</v-icon>
								</v-btn>
								<v-btn
									icon
									color="primary"
									small
									@click="revealSecretValue(item)"
								>
									<v-icon small>
										{{
											item.showSecretValue
												? "visibility_off"
												: "visibility"
										}}
									</v-icon>
								</v-btn>
								<template v-if="item.showSecretValue">
									{{ item.secretValue }}
								</template>
								<template v-else>********</template>
							</template>

							<template v-slot:[`item.url`]="{ item }">
								<template v-if="item.url">
									<v-btn
										icon
										color="primary"
										@click="copyValue(item.url)"
										small
									>
										<v-icon small>content_copy</v-icon>
									</v-btn>
									<span
										v-if="item.url.length <= 30"
										class="row-pointer"
										@click="openUrl(item.url)"
										>{{ item.url }}</span
									>
									<span
										v-else
										class="row-pointer"
										@click="openUrl(item.url)"
										>{{
											item.url.substring(0, 30) + ".."
										}}</span
									>
								</template>
							</template>

							<template v-slot:[`item.action`]="{ item }">
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn
											v-on="on"
											icon
											color="primary"
											small
											@click="editSecret(item)"
										>
											<v-icon small>edit</v-icon>
										</v-btn>
									</template>
									<span> Edit Secret </span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn
											v-on="on"
											icon
											color="primary"
											small
											@click="deletingSecret(item)"
										>
											<v-icon small>delete</v-icon>
										</v-btn>
									</template>
									<span> Delete Secret </span>
								</v-tooltip>
								<v-tooltip bottom>
									<template v-slot:activator="{ on }">
										<v-btn
											v-on="on"
											icon
											color="primary"
											small
											@click="createOneTimeSecret(item)"
										>
											<v-icon small>vpn_key</v-icon>
										</v-btn>
									</template>
									<span> Create One Time Secret </span>
								</v-tooltip>
							</template>

							<template v-slot:expanded-item="{ headers, item }">
								<td :colspan="headers.length" class="px-5 py-5">
									<v-row class="align-center ml-0 pl-0 mb-0">
										<v-col cols="auto">
											<h2>Tags</h2>
										</v-col>
										<v-spacer />
									</v-row>
									<v-divider class="mb-2"></v-divider>
									<v-row dense>
										<v-col cols="6" sm="4" md="2"
											><strong>Notes</strong></v-col
										>
										<v-col cols="6" sm="8" md="10">
											{{ item.notes }}
										</v-col>
									</v-row>
									<template v-for="tag in item.tags">
										<v-row :key="tag.key" dense>
											<v-col cols="6" sm="4" md="2"
												><strong>{{
													tag.key
												}}</strong></v-col
											>
											<v-col cols="6" sm="8" md="10">
												{{ tag.value }}
											</v-col>
										</v-row>
									</template>
								</td>
							</template>
						</v-data-table>
					</v-card-text>
				</v-card>
			</v-container>
		</v-card>

		<DeleteDialog
			:dialog.sync="deleteSecretDialog"
			:item="deletedSecret"
			:deleteError="deleteError"
			v-on:confirmed="deleteSecret"
		>
			<template v-slot:title>Delete - Secret</template>
			<template v-slot:content
				>Are you sure you want to delete this secret?</template
			>
		</DeleteDialog>

		<SecretForm
			:dialog.sync="secretDialog"
			:object.sync="editedSecret"
			:selectedVault.sync="selectedVault"
			v-on:done="getSecrets(selectedVault)"
		></SecretForm>

		<OneTimeSecretForm
			:dialog.sync="oneTimeSecretDialog"
			:object.sync="editedSecret"
		></OneTimeSecretForm>
	</v-container>
</template>

<style scoped>
.row-pointer {
	cursor: pointer;
}
</style>

<script>
import helper from "@/utils/helper.js";
import msalAuth from "@/utils/msalAuth.js";
import axios from "axios";
import DeleteDialog from "@/components/base/DeleteDialog.vue";
import SecretForm from "@/components/secrets/SecretForm.vue";
import OneTimeSecretForm from "@/components/secrets/OneTimeSecretForm.vue";

export default {
	name: "Login",
	components: { DeleteDialog, SecretForm, OneTimeSecretForm },

	data: () => ({
		error: "",
		deleteError: "",

		selectedSubscription: [],
		selectedVault: [],
		selectedSecret: [],
		searchSecret: "",
		defaultSelection: {
			subscriptionId: null,
			vault: null,
		},

		isLoadingSubscriptions: false,
		isLoadingVaults: false,
		isLoadingSecrets: false,

		subscriptions: [],
		vaults: [],
		secrets: [],

		secretDialog: false,
		deleteSecretDialog: false,
		editedSecret: null,
		deletedSecret: null,

		oneTimeSecretDialog: false,

		secretHeaders: [
			{
				text: "Secret Name",
				value: "secretName",
				sortable: true,
			},
			{
				text: "Type",
				value: "type",
				sortable: true,
			},
			{
				text: "Url | Ctrl+[",
				value: "url",
				sortable: false,
			},
			{
				text: "Username | Ctrl+]",
				value: "username",
				sortable: true,
			},
			{
				text: "Secret Value | Ctrl+\\",
				value: "secretValue",
				sortable: false,
			},
			{
				text: "Actions",
				align: "center",
				value: "action",
				sortable: false,
			},
			{ text: "", value: "data-table-expand" },
		],

		errors: [],
		rules: {
			required: (v) => !helper.isEmpty(v) || "This field is required",
			listRequired: (v) =>
				(v || "").length >= 1 || `This field is required`,
			minLength: (len) => (v) =>
				(v || "").length >= len || `Require at least ${len}`,
			maxLength: (len) => (v) => (v || "").length <= len || "Too long",
		},
	}),

	async created() {
		this.getDefaultSelection();
		this.getSearchSecret();
		this.getSubscriptions();
	},

	watch: {
		selectedSubscription(val) {
			if (val) {
				// console.log(val);
				this.getVaults(val);
			}
		},

		selectedVault(val) {
			if (val) {
				this.searchSecret = "";
				this.getSecrets(val);
			}
		},

		searchSecret(val) {
			if (val) {
				this.setSearchSecret(val);
			} else {
				this.clearSearchSecret();
			}
		},
	},

	mounted() {
		this._keyListener = function (e) {
			// Search focus
			if (e.key === ";" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault(); // present "Save Page" from getting triggered.
				this.focusSearchSecret();
			}

			// Copy url
			if (e.key === "[" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault(); // present "Save Page" from getting triggered.
				this.copyValue(this.selectedSecret[0].url);
			}

			// Copy username
			if (e.key === "]" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault(); // present "Save Page" from getting triggered.
				this.copyValue(this.selectedSecret[0].username);
			}

			// Copy password
			if (e.key === "\\" && (e.ctrlKey || e.metaKey)) {
				e.preventDefault(); // present "Save Page" from getting triggered.
				this.copySecretValue(this.selectedSecret[0]);
			}

			// Add secret
			if (e.key === "," && (e.ctrlKey || e.metaKey)) {
				e.preventDefault(); // present "Save Page" from getting triggered.
				this.addSecret();
			}
		};

		document.addEventListener("keydown", this._keyListener.bind(this));
	},

	beforeDestroy() {
		document.removeEventListener("keydown", this._keyListener);
	},

	methods: {
		async getManagementAccessToken(claims) {
			let scopes = [
				"https://management.core.windows.net/user_impersonation",
			];
			try {
				let token = await msalAuth.acquireToken(scopes, claims);
				return token;
			} catch (err) {
				console.log(err);
				this.error = err.toString();
			}
		},

		async getVaultAccessToken(claims) {
			let scopes = ["https://vault.azure.net/user_impersonation"];
			try {
				let token = await msalAuth.acquireToken(scopes, claims);
				return token;
			} catch (err) {
				console.log(err);
				this.error = err.toString();
			}
		},

		async getSubscriptions() {
			let managementAccessToken = await this.getManagementAccessToken();
			this.isLoadingSubscriptions = true;

			try {
				let response = await axios.post(
					`${process.env.VUE_APP_APIURL}azure/management/subscriptions`,
					{
						accessToken: managementAccessToken,
					}
				);
				this.subscriptions = response.data.value;
			} catch (err) {
				const claims = this.getClaimsFromError(err);
				if (claims) {
					try {
						managementAccessToken = await this.getManagementAccessToken(
							claims
						);
						let response = await axios.post(
							`${process.env.VUE_APP_APIURL}azure/management/subscriptions`,
							{
								accessToken: managementAccessToken,
							}
						);
						this.subscriptions = response.data.value;
						return;
					} catch (retryErr) {
						console.log("Retry with claims failed", retryErr);
					}
				}

				console.log(err.response);
				if (err.response && err.response.data) {
					this.error =
						this.error +
						err +
						" | Get Subscriptions | " +
						err.response.data.message;
				} else {
					this.error = this.error + err + " | Get Subscriptions";
				}
			} finally {
				this.isLoadingSubscriptions = false;

				// Restore the saved selection only when the list actually
				// loaded and still contains it. Doing this unconditionally
				// meant a *failed* load still triggered the watcher, cascading
				// into getVaults() and then getSecrets().
				const saved = this.defaultSelection.subscriptionId;
				if (
					saved &&
					this.subscriptions.some((s) => s.subscriptionId === saved)
				) {
					this.selectedSubscription = saved;
				}
			}
		},

		async getVaults(subscriptionId) {
			let managementAccessToken = await this.getManagementAccessToken();
			this.isLoadingVaults = true;

			try {
				let response = await axios.post(
					`${process.env.VUE_APP_APIURL}azure/management/vaults`,
					{
						accessToken: managementAccessToken,
						subscriptionId: subscriptionId,
					}
				);
				this.vaults = response.data.value;
			} catch (err) {
				const claims = this.getClaimsFromError(err);
				if (claims) {
					try {
						managementAccessToken = await this.getManagementAccessToken(
							claims
						);
						let response = await axios.post(
							`${process.env.VUE_APP_APIURL}azure/management/vaults`,
							{
								accessToken: managementAccessToken,
								subscriptionId: subscriptionId,
							}
						);
						this.vaults = response.data.value;
						return;
					} catch (retryErr) {
						console.log("Retry with claims failed", retryErr);
					}
				}

				console.log(err.response);
				if (err.response && err.response.data) {
					this.error =
						this.error +
						err +
						" | Get Vaults | " +
						err.response.data.message;
				} else {
					this.error = this.error + err + " | Get Vaults";
				}
			} finally {
				this.isLoadingVaults = false;

				// Same guard as getSubscriptions: never restore the saved vault
				// off the back of a failed load, or the watcher fires
				// getSecrets() against a list we never received.
				const saved = this.defaultSelection.vault;
				if (
					saved &&
					this.vaults.some(
						(v) => v.properties && v.properties.vaultUri === saved
					)
				) {
					this.selectedVault = saved;
				}
			}
		},

		async getSecrets(vaultUri) {
			let vaultAccessToken = await this.getVaultAccessToken();
			this.isLoadingSecrets = true;

			try {
				let response = await axios.post(
					`${process.env.VUE_APP_APIURL}azure/vault/secrets`,
					{
						accessToken: vaultAccessToken,
						vaultUri: vaultUri,
					}
				);
				// console.log(response);
				this.secrets = response.data.value;
			} catch (err) {
				const claims = this.getClaimsFromError(err);
				if (claims) {
					try {
						vaultAccessToken = await this.getVaultAccessToken(
							claims
						);
						let response = await axios.post(
							`${process.env.VUE_APP_APIURL}azure/vault/secrets`,
							{
								accessToken: vaultAccessToken,
								vaultUri: vaultUri,
							}
						);
						this.secrets = response.data.value;
						return;
					} catch (retryErr) {
						console.log("Retry with claims failed", retryErr);
					}
				}

				console.log(err.response);
				if (err.response && err.response.data) {
					this.error =
						this.error +
						err +
						" | Get Secrets | " +
						err.response.data.message;
				} else {
					this.error = this.error + err + " | Get Secrets";
				}
			} finally {
				this.isLoadingSecrets = false;
			}
		},

		// The secrets list no longer carries values: the backend returns
		// metadata only, so opening a vault costs one upstream call per page
		// instead of one per secret. Values are fetched here, on demand, and
		// cached on the row once retrieved.
		async ensureSecretValue(item) {
			if (item.secretValue !== null && item.secretValue !== undefined) {
				return item.secretValue;
			}

			let vaultAccessToken = await this.getVaultAccessToken();
			const url = `${process.env.VUE_APP_APIURL}azure/vault/secret/value`;
			const body = () => ({
				accessToken: vaultAccessToken,
				secretId: item.secretId,
			});

			try {
				let response = await axios.post(url, body());
				this.$set(item, "secretValue", response.data.value);
				return item.secretValue;
			} catch (err) {
				const claims = this.getClaimsFromError(err);
				if (claims) {
					try {
						vaultAccessToken = await this.getVaultAccessToken(
							claims
						);
						let response = await axios.post(url, body());
						this.$set(item, "secretValue", response.data.value);
						return item.secretValue;
					} catch (retryErr) {
						console.log("Retry with claims failed", retryErr);
					}
				}

				console.log(err.response);
				if (err.response && err.response.data) {
					this.error =
						this.error +
						err +
						" | Get Secret Value | " +
						err.response.data.message;
				} else {
					this.error = this.error + err + " | Get Secret Value";
				}

				return null;
			}
		},

		async revealSecretValue(item) {
			if (item.showSecretValue) {
				item.showSecretValue = false;
				return;
			}

			if ((await this.ensureSecretValue(item)) === null) {
				return;
			}

			item.showSecretValue = true;
		},

		async copySecretValue(item) {
			const value = await this.ensureSecretValue(item);

			if (value !== null) {
				await this.copyValue(value);
			}
		},

		async copyValue(value) {
			try {
				await navigator.clipboard.writeText(value);
				// show snackbar
				const payload = {
					color: "success",
					message: "Copied value",
				};
				this.$store.dispatch("snackbar/showMessage", payload);
			} catch ($e) {
				// show snackbar
				const payload = {
					color: "error",
					message: "Error copying value",
				};
				this.$store.dispatch("snackbar/showMessage", payload);
			}
		},

		focusSearchSecret() {
			this.$refs["searchSecret"].focus();
		},

		openUrl(url) {
			window.open(url, "_blank");
		},

		addSecret() {
			this.editedSecret = null;
			this.secretDialog = true;
		},

		async editSecret(secret) {
			// The form pre-fills the current value, which the list no longer
			// carries - pull it in before opening the dialog.
			if ((await this.ensureSecretValue(secret)) === null) {
				return;
			}

			this.editedSecret = JSON.parse(JSON.stringify(secret));
			this.secretDialog = true;
		},

		deletingSecret(item) {
			this.deletedSecret = item;
			this.deleteSecretDialog = true;
		},

		async deleteSecret(item) {
			let vaultAccessToken = await this.getVaultAccessToken();

			try {
				let response = await axios.post(
					`${process.env.VUE_APP_APIURL}azure/vault/secret/delete`,
					{
						accessToken: vaultAccessToken,
						vaultUri: this.selectedVault,
						secretName: item.secretName,
					}
				);
				// console.log(response);
				this.deleteSecretDialog = false;
				this.getSecrets(this.selectedVault);

				// show snackbar
				const payload = {
					color: "success",
					message: "Deleted Secret",
				};
				this.$store.dispatch("snackbar/showMessage", payload);
			} catch (err) {
				const claims = this.getClaimsFromError(err);
				if (claims) {
					try {
						vaultAccessToken = await this.getVaultAccessToken(
							claims
						);
						let response = await axios.post(
							`${process.env.VUE_APP_APIURL}azure/vault/secret/delete`,
							{
								accessToken: vaultAccessToken,
								vaultUri: this.selectedVault,
								secretName: item.secretName,
							}
						);
						// console.log(response);
						this.deleteSecretDialog = false;
						this.getSecrets(this.selectedVault);

						// show snackbar
						const payload = {
							color: "success",
							message: "Deleted Secret",
						};
						this.$store.dispatch("snackbar/showMessage", payload);
						return;
					} catch (retryErr) {
						console.log("Retry with claims failed", retryErr);
					}
				}

				console.log(err.response);
				if (err.response && err.response.data) {
					this.deleteError =
						this.deleteError +
						err +
						" | Delete Secret | " +
						err.response.data.message;
				} else {
					this.deleteError =
						this.deleteError + err + " | Delete Secret";
				}
			} finally {
			}
		},

		async createOneTimeSecret(secret) {
			// Same as editSecret: the form needs the value, so fetch it first.
			if ((await this.ensureSecretValue(secret)) === null) {
				return;
			}

			this.editedSecret = JSON.parse(JSON.stringify(secret));
			this.oneTimeSecretDialog = true;
		},

		getDefaultSelection() {
			const defaultSelection = JSON.parse(
				localStorage.getItem("secrets/defaultSelection")
			);

			if (defaultSelection) {
				this.defaultSelection = defaultSelection;
			}
		},

		setDefaultSelection() {
			this.defaultSelection.subscriptionId = this.selectedSubscription;
			this.defaultSelection.vault = this.selectedVault;
			localStorage.setItem(
				"secrets/defaultSelection",
				JSON.stringify(this.defaultSelection)
			);
		},

		deleteDefaultSelection() {
			localStorage.removeItem("secrets/defaultSelection");
			this.defaultSelection = {
				subscriptionId: null,
				vault: null,
			};
		},

		getSearchSecret() {
			const searchSecret = localStorage.getItem("secrets/searchSecret");

			if (searchSecret) {
				this.searchSecret = searchSecret;
			}
		},

		setSearchSecret(val) {
			if (val) {
				localStorage.setItem("secrets/searchSecret", val);
			} else {
				localStorage.removeItem("secrets/searchSecret");
			}
		},

		clearSearchSecret() {
			localStorage.removeItem("secrets/searchSecret");
		},

		getClaimsFromError(err) {
			if (
				err.response &&
				err.response.status === 401 &&
				err.response.headers["www-authenticate"]
			) {
				const wwwAuth = err.response.headers["www-authenticate"];
				const match = wwwAuth.match(/claims="([^"]+)"/);
				if (match && match[1]) {
					return match[1];
				}
			}
			return null;
		},
	},

	computed: {},
};
</script>
